import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, MapPin } from 'lucide-react';
import { COUNTRY_DOMAINS, detectCountryDomain } from '../lib/country-domain';

/**
 * Flag dropdown that navigates between the salunas country domains. Static map
 * (works before any salons exist in a country). On salunas.com (all countries)
 * the trigger is a MapPin icon — a 🌐 emoji here sat right next to the
 * LanguageSwitcher's globe and read as a duplicate control.
 * Cross-origin nav to each domain's home (no path preservation in v1).
 *
 * Mirrors LanguageSwitcher's interaction pattern (click-toggle + outside-click
 * dismiss + role=listbox + Check on active) so the two header controls behave
 * identically side by side.
 */
export interface CountrySwitcherProps {
  /**
   * - `icon`   (default) flag / pin icon only
   * - `pill`   outline pill with a pin + country name — redesign top nav
   * - `domain` footer pill "BE — salunas.be"; the menu opens upwards
   */
  variant?: 'icon' | 'pill' | 'domain';
}

export function CountrySwitcher({ variant = 'icon' }: CountrySwitcherProps = {}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const current = detectCountryDomain().code;
  const currentDomain = COUNTRY_DOMAINS.find((d) => d.code === current) ?? null;
  const currentFlag = currentDomain?.flag ?? null;
  const currentName = currentDomain ? t(`countrySwitcher.${currentDomain.i18nKey}`) : t('countrySwitcher.all');
  const currentHost = currentDomain ? currentDomain.url.replace(/^https?:\/\//, '') : 'salunas.com';

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (url: string) => {
    window.location.assign(url);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={
          variant === 'icon'
            ? t('countrySwitcher.label')
            : `${t('countrySwitcher.label')}: ${variant === 'domain' ? currentHost : currentName}`
        }
        aria-expanded={open}
        aria-haspopup="listbox"
        className={
          variant === 'pill'
            ? 'inline-flex h-ctl-sm items-center gap-2 rounded-full border border-sl-line-strong bg-transparent px-4 text-[14px] font-medium text-sl-ink transition-colors hover:bg-sl-surface'
            : variant === 'domain'
              ? 'hit-44 inline-flex h-8 items-center rounded-full border border-sl-on-dark-muted/35 px-3 text-[12.5px] font-semibold text-sl-on-dark-chip transition-colors hover:border-sl-on-dark-muted/70'
              : 'inline-flex items-center px-2.5 py-1.5 rounded-full text-base text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors'
        }
      >
        {variant === 'pill' ? (
          <>
            <MapPin className="h-4 w-4" aria-hidden="true" />
            <span>{currentName}</span>
          </>
        ) : variant === 'domain' ? (
          <span>{current ? `${current} — ${currentHost}` : currentHost}</span>
        ) : currentFlag ? (
          <span aria-hidden="true">{currentFlag}</span>
        ) : (
          <MapPin className="w-4 h-4" aria-hidden="true" />
        )}
      </button>

      {open && (
        <ul
          role="listbox"
          className={[
            'absolute right-0 min-w-[180px] rounded-xl border border-border bg-card shadow-lg overflow-hidden z-50',
            variant === 'domain' ? 'bottom-full mb-2' : 'top-full mt-2',
          ].join(' ')}
        >
          {COUNTRY_DOMAINS.map((d) => {
            const isActive = d.code === current;
            return (
              <li key={d.code}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    go(d.url);
                  }}
                  className={[
                    'w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm transition-colors',
                    isActive
                      ? 'bg-foreground/[0.04] text-foreground'
                      : 'text-foreground hover:bg-secondary',
                  ].join(' ')}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-base" aria-hidden="true">{d.flag}</span>
                    <span className="font-medium">{t(`countrySwitcher.${d.i18nKey}`)}</span>
                  </span>
                  {isActive && <Check className="w-4 h-4 text-primary" />}
                </button>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                go('https://salunas.com');
              }}
              className={[
                'w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm transition-colors',
                current === null
                  ? 'bg-foreground/[0.04] text-foreground'
                  : 'text-foreground hover:bg-secondary',
              ].join(' ')}
            >
              <span className="flex items-center gap-2">
                <span className="text-base" aria-hidden="true">🌐</span>
                <span className="font-medium">{t('countrySwitcher.all')}</span>
              </span>
              {current === null && <Check className="w-4 h-4 text-primary" />}
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
